/**
 * Demolition physics tuning constants. Extracted from demolitionPhysics.ts.
 */

import type { Vec3 } from '../../services/fracture/voronoiFracture';
import {
  BLAST_VOLLEY_SUB_BUDGET as BLAST_VOLLEY_SUB_BUDGET_SHARED,
  CANNONBALL_DENSITY as CANNONBALL_DENSITY_SHARED,
  CANNONBALL_RADIUS as CANNONBALL_RADIUS_SHARED,
  CANNONBALL_SPEED as CANNONBALL_SPEED_SHARED,
  CHARGE_DENSITY as CHARGE_DENSITY_SHARED,
  CHARGE_RADIUS as CHARGE_RADIUS_SHARED,
  CHARGE_SPEED as CHARGE_SPEED_SHARED,
  CONCUSSION_DENSITY as CONCUSSION_DENSITY_SHARED,
  CONCUSSION_RADIUS as CONCUSSION_RADIUS_SHARED,
  CONCUSSION_SPEED as CONCUSSION_SPEED_SHARED,
  MAX_DYNAMIC_CHUNKS as MAX_DYNAMIC_CHUNKS_SHARED,
  MAX_LAUNCH_SPEED as MAX_LAUNCH_SPEED_SHARED,
  MAX_LIVE_CELLS_MUL as MAX_LIVE_CELLS_MUL_SHARED,
  MAX_SUBFRACTURE_BODIES_PER_SHOT as MAX_SUBFRACTURE_BODIES_PER_SHOT_SHARED,
  RUBBLE_DENSITY as RUBBLE_DENSITY_SHARED,
  RUBBLE_RADIUS as RUBBLE_RADIUS_SHARED,
  RUBBLE_SPEED as RUBBLE_SPEED_SHARED,
  SPIKE_DENSITY as SPIKE_DENSITY_SHARED,
  SPIKE_RADIUS as SPIKE_RADIUS_SHARED,
  SPIKE_SPEED as SPIKE_SPEED_SHARED,
} from './demolitionPhysicsConstants';

// ---------------------------------------------------------------------------
// Constants (concrete defaults kept as exports for tests / backward compat)
// ---------------------------------------------------------------------------

export const DEMO_FIXED_DT = 1 / 60;
export const DEMO_SUBSTEPS = 4;

/** Default concrete wall — kept for callers that import module-level dims. */
export const WALL_FULL = { x: 6.0, y: 4.2, z: 0.6 };
export const WALL_HALF: Vec3 = {
  x: WALL_FULL.x / 2,
  y: WALL_FULL.y / 2,
  z: WALL_FULL.z / 2,
};
/** Wall center so bottom sits on y=0 (concrete default). */
export const WALL_CENTER: Vec3 = { x: 0, y: WALL_HALF.y, z: 0 };

/** Non-uniform partition jitter (fraction of local spacing). */
export const SEED_JITTER = 0.4;
/** Through-thickness layers (thin wall). */
export const SEED_Z_LAYERS = 2;
/** Face cell size band for non-uniform spacings (m). */
export const SEED_MIN_SPACING = 0.28;
export const SEED_MAX_SPACING = 1.2;

export const CONCRETE_DENSITY = 2300;
export const PROXY_SHRINK = 0.88;
export const PROXY_SHRINK_THIN = 0.75;
export const THIN_VOLUME_RATIO = 0.55;

export const CHUNK_LINEAR_DAMPING = 0.05;
export const CHUNK_ANGULAR_DAMPING = 0.1;
export const CHUNK_FRICTION = 0.78;
export const CHUNK_RESTITUTION = 0.04;
/**
 * Dynamic-chunk collider linear scale. Voronoi cells tile with zero gap, so a
 * full-size hull starts face-contact locked with every neighbour and contact
 * resolution cancels launch velocity in one step. Visual meshes stay full-size.
 */
export const CHUNK_HULL_SHRINK = 0.94;
/** Distance (m) a released chunk must travel from spawn to count as displaced. */
export const RELEASED_DISPLACED_EPS = 0.15;
/**
 * When a released cell's spawn centroid is within this radius of an embedded
 * spike, the spike is restored to dynamic and falls with the rubble.
 */
export const SPIKE_UNEMBED_RADIUS = 0.8;

export const IMPACT_SPEED_THRESHOLD = 8;
export const IMPULSE_UPWARD_BIAS = 0.15;

export const MAX_DYNAMIC_CHUNKS = MAX_DYNAMIC_CHUNKS_SHARED;
/** Max physics bodies created from sub-fracture per shot (concrete default). */
export const MAX_SUBFRACTURE_BODIES_PER_SHOT = MAX_SUBFRACTURE_BODIES_PER_SHOT_SHARED;
/** Sub-chunks below this volume (m³) become ballistic shard particles. */
export const SHARD_VOLUME_FLOOR = 0.003;

export const MAX_CANNONBALLS = 6;
/** Small, visible-but-bounded restitution for a projectile striking loose concrete. */
export const RUBBLE_CONTACT_RESTITUTION = 0.12;
/**
 * Collision density for the visible steel cannonball. Damage remains calibrated
 * to CANNONBALL_DENSITY/28 m/s below; keeping those two concerns separate gives
 * the solver a ball that can move concrete without changing fracture tuning.
 */
export const CANNONBALL_PHYSICS_DENSITY = 3_500;
export const CANNONBALL_RADIUS = CANNONBALL_RADIUS_SHARED;
export const CANNONBALL_DENSITY = CANNONBALL_DENSITY_SHARED;
export const CANNONBALL_SPEED = CANNONBALL_SPEED_SHARED;

/** Bounded weapon intensity multiplier shared by desktop and mobile controls. */
export const DEFAULT_WEAPON_POWER = 1;
export const MIN_WEAPON_POWER = 0.75;
export const MAX_WEAPON_POWER = 1.75;

/** Keep a post-fracture projectile impact visually immediate and bounded. */
export const SUBSEQUENT_IMPACT_SUB_BUDGET = 10;
/** Lateral offset (m) from the blast origin: beside the crater, not in it. */
export const FRAMED_BOULDER_LATERAL_M = 0.9;
/** World Y (m) above the base-blast pile band. */
export const FRAMED_BOULDER_KEEP_Y = 1.0;

export const RUBBLE_TRANSMISSION_TRANSFER = 0.45;
/**
 * How far in front of the wall face a rubble contact can still transmit
 * damage into the wall (m). Transfer falls off linearly across this depth:
 * packed debris against the face passes most momentum; a heap a meter out
 * passes none. Keeps the crater from becoming an invulnerable rampart while
 * still rewarding aim above the debris line.
 */
export const RUBBLE_TRANSMISSION_DEPTH = 1.0;
export const FEEDBACK_SPEED_THRESHOLD = 2;

export const SPIKE_RADIUS = SPIKE_RADIUS_SHARED;
export const SPIKE_DENSITY = SPIKE_DENSITY_SHARED;
export const SPIKE_SPEED = SPIKE_SPEED_SHARED;
export const RUBBLE_RADIUS = RUBBLE_RADIUS_SHARED;
export const RUBBLE_DENSITY = RUBBLE_DENSITY_SHARED;
export const RUBBLE_SPEED = RUBBLE_SPEED_SHARED;
export const CHARGE_RADIUS = CHARGE_RADIUS_SHARED;
export const CHARGE_DENSITY = CHARGE_DENSITY_SHARED;
export const CHARGE_SPEED = CHARGE_SPEED_SHARED;
export const CONCUSSION_RADIUS = CONCUSSION_RADIUS_SHARED;
export const CONCUSSION_DENSITY = CONCUSSION_DENSITY_SHARED;
export const CONCUSSION_SPEED = CONCUSSION_SPEED_SHARED;

/** Fuse time after place before first detonation (s). */
export const BLAST_FUSE_SEC = 1.0;
/** Stagger between multi-charge detonations (s). */
export const BLAST_STAGGER_SEC = 0.35;
/** Max live charges placeable while any fuse burns. */
export const MAX_CHARGES = 3;
/** Shared sub-fracture physics-body budget per multi-charge volley. */
export const BLAST_VOLLEY_SUB_BUDGET = BLAST_VOLLEY_SUB_BUDGET_SHARED;
/** Synthetic approach-speed equivalent for blast VFX / lastImpact.speed. */
export const BLAST_EQUIV_SPEED = 36;
/** Hard clamp on chunk launch speed after carve/blast impulses (m/s). */
export const MAX_LAUNCH_SPEED = MAX_LAUNCH_SPEED_SHARED;
/** Keep clamping dynamics for this many sim-seconds after carve/blast. */
export const LAUNCH_CLAMP_WINDOW_SEC = 1.5;
/** Live cells hard cap multiplier vs pristine partition size (concrete 140 → 560). */
export const MAX_LIVE_CELLS_MUL = MAX_LIVE_CELLS_MUL_SHARED;
/** Pre-crack sub-pieces when accumulated damage ≥ 1. */
export const PRECRACK_PIECES = 4;

export const GROUND_EPS = 0.08;
export const COLLAPSE_DELAY_MIN = 0.15;
export const COLLAPSE_DELAY_MAX = 0.35;

export const FADE_DURATION = 0.5;

/**
 * Periodic sleep sweep. Dynamic rubble may sleep only after the normal
 * displacement/velocity checks below. There is no delayed height-based sleep:
 * a fragment can remain lodged in a wall opening and must still be wakeable by
 * a later projectile impact.
 */
export const SLEEP_SWEEP_INTERVAL = 2;
export const SLEEP_MOVE_EPS = 0.01;
/** Low-energy residual: |v| below this for a full sweep window → sleep. */
export const SLEEP_VEL_EPS = 0.05;
/** Consecutive slow/low-energy sweeps required before sleep (mid-air apex
 *  can't pass two full 2s windows of <1cm motion under gravity). */
export const SLEEP_SLOW_STREAK = 1;

/** A live projectile that stops inside the wall envelope needs a deterministic escape. */
export const PROJECTILE_STALL_SPEED_EPS = 0.25;
export const PROJECTILE_STALL_RECOVERY_COOLDOWN = 0.35;
export const PROJECTILE_WALL_CAGE_MARGIN = 0.25;
/**
 * Seconds below flight speed before a spent projectile retires with a puff.
 * Keeps the 6-slot pool clearing itself so old balls stop cushioning the wall.
 */
export const PROJECTILE_REST_DWELL_SEC = 1.2;
/**
 * Flight-speed floor (m/s). At or above it a projectile is live and its rest
 * dwell resets; below it the ball is spent and counts down to retirement.
 * Every weapon launches at >=36 m/s, so live shots are never reaped in flight.
 */
export const PROJECTILE_REST_RESET_SPEED = 2;
/** Stall recoveries allowed per ball before it retires instead of re-ejecting. */
export const PROJECTILE_MAX_RECOVERIES = 2;
export const MAX_IMPACT_TRACES = 8;

export function clampWeaponPower(power: number): number {
  if (!Number.isFinite(power)) return DEFAULT_WEAPON_POWER;
  return Math.min(MAX_WEAPON_POWER, Math.max(MIN_WEAPON_POWER, power));
}
